import 'ol/ol.css';
import {Vector as VectorSource} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import {Point, Polygon} from "ol/geom";
import { ProjectedCoordinate } from '../projections';
import {Feature} from "ol";
import {
  Circle as CircleStyle,
  Fill, Stroke, Style
} from "ol/style";
import { AerialMap, extentFromPoints } from '../aerial/map';
import { textToColor } from './categories';
import { checkValidPolygon } from "../utilities/polygon";
import { transform } from 'ol/proj';
import { ImageLabelTools } from '.';
import {
  annotationFromPolygon,
  CocoImage,
  ImageLabel,
  LabelCategory,
  sectionsFromIntersection
} from './image-label';
import { Color } from 'three';
import {polygon as turfPolygon, intersect as turfIntersect}from '@turf/turf';

class LabelPoint extends Feature {
  public label: OrthoLabel;
  public index;

  constructor(opts, label, index) {
    super(opts);
    this.label = label;
    this.index = index;
  }
}

class LabelPolygon extends Feature {
  public label: OrthoLabel;

  constructor(opts, label) {
    super(opts);
    this.label = label;
  }
}

class Coordinate {
  private array = [];

  constructor(coordinates) {
    this.array = coordinates;
  }

  get x() {
    return this.array[0];
  }

  get y() {
    return this.array[1];
  }

  get point() {
    return this.array;
  }

  update(point) {
    if (point.length === 2) {
      this.array = point;
    }
  }
}

class OrthoLabel extends ImageLabel {
  private coordinates: Coordinate[] = [];
  public aerialMap: AerialMap;
  public polygonLayer: VectorLayer;
  public pointLayer: VectorLayer;
  private minCoordinates = 3;
  private minPixelDistance = 5;

  constructor(category: OrthoCategory, aerialMap: AerialMap) {
    super(category);
    this.aerialMap = aerialMap;
  }

  get hasEnoughPoints() {
    return this.numPoints >= this.minCoordinates;
  }

  get numPoints() {
    return this.coordinates.length;
  }

  get coords() {
    /** Used for polygon simplification */
    return this.coordinates.map(x => x.point);
  }

  get polygonPointsOnly() {
    return this.coordinates.map(x => x.point);
  }

  get map() {
    return this.aerialMap.map;
  }

  get darkenedColor() {
    let pointColor = this.categoryColor(1.0);
    let dark = new Color(pointColor)
      .multiplyScalar(0.75);

    return `rgb(${dark.r*256}, ${dark.g*256}, ${dark.b*256})`;
  }

  categoryColor(opacity=0.2) {
    if (this.highlighted) {
      return `rgba(${Math.ceil(255)}, ${Math.ceil(255)}, ${Math.ceil(255)}, ${opacity})`;
    } else {
      return textToColor(this.category.name, opacity);
    }
  }

  pointStyler(opacity=0.8) {
    let pointColor = this.categoryColor(opacity);
    const strokeColor = 'rgba(0, 0, 0, 1.0)';
    let style = new Style({
      image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({
          color: strokeColor
        }),
        fill: new Fill({
          color: pointColor
        }),
      })
    });

    return style;
  }

  updatePoint(point, index) {
    this.coordinates[index].update(point);
  }

  removePoint() {
    this.coordinates.pop();
  }

  removeSpecificPoint(index) {
    this.coordinates.splice(index, 1);
    this.draw();
  }

  addPoint(point, checkDuplicate=true) {
    const newPoint = new Coordinate(point);
    const oldPoint = this.coordinates[this.coordinates.length - 1];

    if (checkDuplicate && oldPoint) {
      // Check for distance from previous point
      let pixelNew = this.map.getPixelFromCoordinate(newPoint.point);
      let pixelOld = this.map.getPixelFromCoordinate(oldPoint.point);

      let dU = pixelNew[0] - pixelOld[0];
      let dV = pixelNew[1] - pixelOld[1];
      let distance = Math.sqrt(dU**2 + dV**2);

      if (distance < this.minPixelDistance) {
        return true;
      }
    }

    this.coordinates.push(newPoint);
    return false;
  }

  drawPoints(temporaryPoint) {
    this.map.removeLayer(this.pointLayer);

    const pointSource = new VectorSource({
      wrapX: false,
      noWrap: true
    });

    const pointStyle = this.pointStyler();

    let positions = this.coordinates;
    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint];
    }

    positions.forEach((point, index) => {
      const feat = new LabelPoint({
        geometry: new Point([point.x, point.y])
      }, this, index);
      pointSource.addFeature(feat);
    });

    this.pointLayer = new VectorLayer({
      source: pointSource,
      style: pointStyle,
      updateWhileInteracting: false,
      updateWhileAnimating: false
    });

    this.pointLayer.setZIndex(1);
    this.map.addLayer(this.pointLayer);
  }

  drawPolygon(temporaryPoint) {
    this.map.removeLayer(this.polygonLayer);

    const polySource = new VectorSource({
      wrapX: false,
      noWrap: true
    });

    let positions = this.polygonPointsOnly;
    if (temporaryPoint && !this.completed) {
      positions = [...positions, temporaryPoint.point];
    }

    let oldState = this.valid;

    if (this.hasEnoughPoints){
      this.valid = checkValidPolygon(positions);

      if (this.valid !== oldState) {
        this.category.setCategories();
      }
    }

    let feature = new LabelPolygon({
      geometry: new Polygon([positions])
    }, this);

    polySource.addFeature(feature);

    let opacity = this.valid ? 0.2 : 0.0;

    this.polygonLayer = new VectorLayer({
      source: polySource,
      style: new Style({
        fill: new Fill({
          color: this.categoryColor(opacity)
        }),
        stroke: new Stroke({
          color: this.darkenedColor,
          lineDash: this.completed ? undefined : [10, 10],
          width: 2
        }),
      }),
      updateWhileInteracting: false,
      updateWhileAnimating: false
    });

    this.polygonLayer.setZIndex(1);
    this.map.addLayer(this.polygonLayer);
  }

  draw(temporaryPoint=null) {
    if (!this.drawable) {
      return;
    }

    this.drawPoints(temporaryPoint);
    this.drawPolygon(temporaryPoint);
  }

  clear() {
    this.map.removeLayer(this.polygonLayer);
    this.map.removeLayer(this.pointLayer);
  }

  zoom() {
    let positions = [...this.polygonPointsOnly];
    let extent = extentFromPoints(positions, false);
    this.aerialMap.setMapExtent(extent);
  }

  setHighlighted(highlight) {
    this.highlighted = highlight;
    this.draw();
  }
}

class OrthoCategory extends LabelCategory {
  private aerialTools: AerialLabelTools;
  public labels: OrthoLabel[] = [];

  constructor(aerialTools: AerialLabelTools, name) {
    super(name);
    this.aerialTools = aerialTools;
  }

  get aerialMap() {
    return this.aerialTools.aerialMap;
  }

  setCategories() {
    this.aerialTools.parent.setCategories();
  }

  addNewLabel(drawLabel) {
    let label = new OrthoLabel(this, this.aerialMap);
    label.setDrawable(drawLabel);
    this.labels.push(label);

    return label;
  }
}

export class AerialLabelTools {
  public type = "ortho";
  public enabled = false;
  public parent: ImageLabelTools;
  public setPointDelete;
  public setPointAdd;
  public setCategoryList;
  public setAerialTooltipText;
  public contextMenuOpen = false;
  private dragObject: LabelPoint;
  private temporaryPoint: Coordinate;
  private contextMenuClick;


  private startTooltip = "Click to start drawing polygon";

  private dragTooltip = `Click and hold to move vertex
                         <br>Right Click to delete point`;

  private highlightTooltip = `Right Click to select label
                              <br>CTRL + Right Click to select multiple labels`;

  constructor(parent: ImageLabelTools, props) {
    const {setCategoryList, setAerialTooltipText, contextMenuClick} = props;

    this.parent = parent;
    this.setCategoryList = setCategoryList;
    this.setAerialTooltipText = setAerialTooltipText;
    this.contextMenuClick = contextMenuClick;

    this.init();
  }

  get categories() {
    return this.parent.categories
      .filter(x => x instanceof OrthoCategory) as OrthoCategory[];
  }

  get viewer() {
    return this.parent.viewer;
  }

  get aerialMap() {
    return this.viewer.minimap;
  }

  get map() {
    return this.aerialMap.map;
  }

  get activeLabel() {
    let selected = null;
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        if (label.selected) {
          selected = label;
        }
      });
    });

    return selected;
  }

  get numberLabels() {
    let count = 0;
    this.categories.forEach(category =>
      category.labels.forEach(label => count++));
    return count;
  }

  get moreTooltip() {
    return `
      Click to add more points (${this.numPoints})
      <br>Delete Key to remove last point
      ${(this.numPoints > 1) ? "<br>Double-click to finish" : ""}
      <br>Right-click to remove label
    `;
  }

  get invalidTooltip() {
    return `
      Polygon is invalid due to self-intersection
      <br>Click to add more points (${this.numPoints})
      <br>Delete Key to remove last point
      ${(this.numPoints > 1) ? "<br>Double-click to finish" : ""}
      <br>Right-click to remove label
    `;
  }

  get numPoints() {
    let pointCount = 0;
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          pointCount = label.numPoints;
        }
      })
    );
    return pointCount;
  }

  get selected() {
    let selected = false;
    this.categories.forEach(category =>
      category.labels.forEach(label =>
        selected = (selected || label.selected)
      )
    );

    return selected;
  }

  get visibleOrthos() {
    let orthoList = this.viewer.orthoList;
    return orthoList.orthos.filter(x => x.visible);
  }

  get activeImagePaths() {
    return this.visibleOrthos.map(ortho => ortho.path);
  }

  get loadedImagePaths() {
    let orthoList = this.viewer.orthoList;
    return orthoList.orthos.map(ortho => ortho.path);
  }

  init() {
    this.map.on('click', event => {
      this.mouseClick(event);
    });

    this.map.on('dblclick', event => {
      if (!this.enabled) {
        return;
      }

      event.preventDefault();
    });

    // Check for mouse move
    this.map.on('pointermove', event => {
      this.onMouseMove(event);
    });

    // Check for hotkeys
    window.addEventListener('keydown', event => {
      this.onKeyDown(event);
    }, false);

    // Check for right click
    window.addEventListener('mouseup', event => {
      this.onMouseUp(event);
    }, false);
  }

  completeLabel(label) {
    this.resetTemporaryPoint();
    this.parent.completeLabel(label.id);
    this.markLastPointDraggable(label);
  }

  markLastPointDraggable(label) {
    let features = label.pointLayer.getSource().getFeatures();
    let lastPoint = features[features.length - 1];
    this.setDragObject(lastPoint);
  }

  setState(state) {
    this.enabled = state;
    this.resetToolTipText();
  }

  setDragObject(object) {
    if (object instanceof LabelPoint) {
      this.dragObject = object;
      this.aerialMap.setDragState(false);
      this.setPointGrabTooltip();
    } else if (object instanceof LabelPolygon) {
      this.setHighlightTooltip();
    } else {
      this.dragObject = object;
      this.aerialMap.setDragState(true);
      this.resetToolTipText();
    }
  }

  mouseClick(event) {
    if (!this.enabled) {
      return;
    }

    if (!this.selected) {
      return;
    }

    let label = this.activeLabel;
    if (!label) {
      return;
    }

    let duplicatePoint = label.addPoint(event.coordinate);
    if (duplicatePoint && label.hasEnoughPoints) {
      this.completeLabel(label);
      return;
    }

    this.setToolTipText();
    this.resetTemporaryPoint();
    this.drawActiveLabel();
  }

  onMouseMove(event) {
    if (!this.enabled) {
      return;
    }

    this.resetTemporaryPoint();

    // Drawing new points
    if (this.selected) {
      this.temporaryPoint = new Coordinate(event.coordinate);
      this.setToolTipText();
      this.drawActiveLabel();
      return;
    }

    // Dragging existing point
    if (event.dragging) {
      if (this.dragObject) {
        let {label, index} = this.dragObject;
        label.updatePoint(event.coordinate, index);
        label.draw();
      }

      return;
    }

    // Hovering over label
    const features = this.map.getFeaturesAtPixel(event.pixel);
    let featuresPoly = features.filter(x => x instanceof LabelPolygon);
    let featuresPoint = features.filter(x => x instanceof LabelPoint);
    const hoveringPoint = featuresPoint.length > 0;
    const hoveringPoly = featuresPoly.length > 0;

    if (hoveringPoint) {
      this.setDragObject(featuresPoint[0]);
    } else if (hoveringPoly) {
      this.setDragObject(featuresPoly[0]);
    } else {
      this.setDragObject(null);
    }
  }

  onMouseUp(event) {
    if (!this.enabled) {
      return;
    }

    if (event.button === 0) {
      this.contextMenuOpen = false;
    }

    let label = this.activeLabel;
    const isRightClick = (event.button === 2);
    const noneHighlighted = (this.parent.numHighlighted() === 0);
    const oneHighlighted = (this.parent.numHighlighted() === 1);

    if (!label && !this.selected && isRightClick) {
      if (noneHighlighted && !event.ctrlKey && !this.contextMenuOpen) {
        let highlightedFeature =  this.highlightLabel(event);

        if (highlightedFeature) {
          let isPoint = (highlightedFeature instanceof LabelPoint) ? highlightedFeature.index : null;
          let highlightedLabel = highlightedFeature.label;
          this.contextMenuClick(event, isPoint, highlightedLabel.id);
          this.contextMenuOpen = true;
        }
      }

      else if (oneHighlighted && !event.ctrlKey && !this.contextMenuOpen) {
        this.parent.unHighlightAll();
        let highlightedFeature =  this.highlightLabel(event);

        if (highlightedFeature) {
          let isPoint = (highlightedFeature instanceof LabelPoint) ? highlightedFeature.index : null;
          let highlightedLabel = highlightedFeature.label;
          this.contextMenuClick(event, isPoint, highlightedLabel.id);
          this.contextMenuOpen = true;
        }
      }

      else if (event.ctrlKey && !this.contextMenuOpen) {
        this.highlightLabel(event);
      }

      else {
        this.contextMenuClick(event);
        this.contextMenuOpen = true;
      }

    }

    else if (isRightClick && (this.selected)) {
      this.parent.deleteLabel(label.id);
    }

    else {
      return;
    }
  }

  onKeyDown(event) {
    this.contextMenuOpen=false;
    // Check for completion hotkey -> Enter
    if (event.which === 13) {
      let label = this.activeLabel;
      if (this.selected){
        this.parent.completeLabel(label.id);
      }
    }

    // Check for deletion hotkey -> Delete
    if (event.which === 46) {
      this.deletePoint();
    }
  }

  setToolTipText() {
    const started = (this.numPoints === 0);
    let helpMsg = started ? this.startTooltip : this.moreTooltip;
    helpMsg = this.activeLabel.valid ? helpMsg : this.invalidTooltip;
    this.setAerialTooltipText(helpMsg);
  }

  highlightLabel(event) {
    let features = this.map.getFeaturesAtPixel([event.offsetX, event.offsetY]);
    let featuresPoint = features.filter(x => (x instanceof LabelPoint));
    let featuresPoly = features.filter(x => (x instanceof LabelPolygon));
    features = featuresPoint.concat(featuresPoly);

    if (features.length > 0) {
      features[0].label.highlighted = !(features[0].label.highlighted);
      features[0].label.draw();
      this.parent.setCategories();
      return features[0];
    }

    return;
  }

  resetToolTipText() {
    this.setAerialTooltipText("");
  }

  setPointGrabTooltip() {
    this.setAerialTooltipText(this.dragTooltip);
  }

  setHighlightTooltip() {
    this.setAerialTooltipText(this.highlightTooltip);
  }

  drawAll() {
    this.categories.forEach(category => {
      category.labels.forEach(label => {
        label.draw(this.temporaryPoint);
      });
    });
  }

  drawActiveLabel() {
    if (!this.activeLabel) {
      return;
    }

    this.activeLabel.draw(this.temporaryPoint);
  }

  getNewCategory(name) {
    return new OrthoCategory(this, name);
  }

  deletePoint() {
    this.categories.forEach(category =>
      category.labels.forEach(label => {
        if (label.selected) {
          label.removePoint();
          this.setToolTipText();
          this.drawActiveLabel();
        }
      })
    );

    this.parent.setCategories();
  }

  deleteSpecificPoint(id) {
    this.categories.forEach(category =>
      category.labels.forEach(label => {

        if (label.highlighted && (label.numPoints > 3)) {
          label.removeSpecificPoint(id);
          this.parent.unHighlight(label.id);
        }
      })
    );

    this.parent.setCategories();
  }

  resetTemporaryPoint() {
    this.temporaryPoint = null;
  }

  polygonInImage = (polygonPoints, imageList) => {
    let imagesWithPolygon = [];
    let imageIds = [];
    let polyPointRing = [...polygonPoints, polygonPoints[0]];

    imageList.forEach((image, imageId) => {
      let pointsOriginalProj = image.transform(polyPointRing);
      const polygonInPixels = this.calculatePolygonPixels(
        pointsOriginalProj, image.metadata);

      let imageInPixels = [
        [0, 0],
        [0, image.originalSize[1]],
        [image.originalSize[0], image.originalSize[1]],
        [image.originalSize[0], 0],
        [0, 0]
      ];

      let intersection = turfIntersect(
        turfPolygon([polygonInPixels]),
        turfPolygon([imageInPixels])
      );

      let intersectionData = sectionsFromIntersection(intersection);
      intersectionData.forEach((arr) => {
        imagesWithPolygon.push(arr);
        imageIds.push(imageId);
      });
    });

    return [imagesWithPolygon, imageIds];
  }

  calculatePolygonPixels = (polygonPoints, metadata) => {
    let pixelPolygon = [];

    polygonPoints.forEach(point => {
      let pixelPoint = [((point[0] - metadata.x_offset)/metadata.x_scale),
        ((point[1] - metadata.y_offset)/metadata.y_scale)];
      pixelPolygon.push(pixelPoint);
    });
    return pixelPolygon;
  }

  pointsToAerial(points, image, projection): Array<[number, number]> {
    let newPoints = [];

    try {
      let metadata = image.metadata;
      newPoints = points.map(point => {
        let x = point[0]*metadata.x_scale + metadata.x_offset;
        let y = point[1]*metadata.y_scale + metadata.y_offset;
        let lonlat = new ProjectedCoordinate([x,y,0], projection).toLonLat();
        return transform([lonlat.x, lonlat.y], 'EPSG:4326', 'EPSG:3857');
      });
    } catch {
      // Do Nothing
    }

    return newPoints;
  }

  jsonImageObj() {
    let images = this.visibleOrthos.map((ortho, imageIndex) => {
      return {
        height: ortho.originalSize[1],
        width: ortho.originalSize[0],
        id: imageIndex,
        file_name: ortho.orthoName,
        metadata: ortho.metadata
      } as CocoImage;
    });

    return images;
  }

  jsonLabelsObj() {
    let labelIndex = 1;
    let annotations = [];

    this.categories.forEach((category, categoryID) => {
      category.labels.forEach(label => {
        if (!label.valid) {
          return;
        }

        const [polygonsInside, imageIds] = this.polygonInImage(
          label.polygonPointsOnly, this.visibleOrthos);

        polygonsInside.forEach((polygon, index) => {
          let annotation = annotationFromPolygon(polygon,
            labelIndex, categoryID, imageIds[index]);
          annotations.push(annotation);
          labelIndex++;
        });
      });
    });

    return annotations;
  };
}
